import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import LocaleFormat from '../utils/LocaleFormat.js';
import { PERIODO_PERSONALIZADO, PERIODO_TODO } from './periodos.js';

/**
 * Selector de período: un desplegable de atajos ("Hoy", "Últimos 30 días", "Este mes"...)
 * cuya última fila es un rango personalizado con dos DatePicker y sus botones.
 *
 * Es el control que estaba copiado como `GravatarOption` en seis features (tickets,
 * combustibles, infracciones, reportes, dashboards, formularios). Acá está una sola vez y
 * como componente funcional; las copias siguen existiendo y no se tocan.
 *
 * El valor es la OPCIÓN elegida, no un rango: convertirla en fechas es tarea de
 * `rangoDePeriodo` al serializar. Solo el período personalizado lleva `inicio`/`fin`
 * adentro, porque son los únicos que no se pueden derivar del id.
 */

const FORMATO = 'YYYY-MM-DD';

/**
 * La fila del rango personalizado. Mantiene sus dos fechas en estado local y recién las
 * entrega al apretar "Aplicar": mientras se elige, el filtro de arriba no se toca.
 */
const FilaPersonalizada = (props) => {
	const intl = useIntl();
	const [inicio, setInicio] = useState(moment().subtract(30, 'days').format(FORMATO));
	const [fin, setFin] = useState(moment().format(FORMATO));

	const aplicar = () => {
		const etiqueta =
			intl.formatMessage({ id: 'gravatarOption.date_filter_period.del', defaultMessage: 'Del ' }) +
			moment(inicio).format('DD-MM-YY') +
			intl.formatMessage({ id: 'gravatarOption.date_filter_period.al', defaultMessage: ' al ' }) +
			moment(fin).format('DD-MM-YY');
		props.onAplicar({ id: PERIODO_PERSONALIZADO, inicio: inicio, fin: fin, label: etiqueta });
	};

	// Cancelar no deja el campo vacío: elige "Todo", que es lo que hacía el control viejo.
	const cancelar = () => {
		props.onAplicar({
			id: PERIODO_TODO,
			label: intl.formatMessage({ id: 'Todo', defaultMessage: 'Todo' }),
		});
	};

	const popperModifiers = {
		offset: { enabled: true, offset: '5px, 10px' },
		preventOverflow: { enabled: true, escapeWithReference: false, boundariesElement: 'viewport' },
	};

	return (
		<div style={{ width: '100%' }}>
			<div className="row">
				<div className="col-6">
					<FormattedMessage id="gravatarOption.date_filter_period.label_desde" defaultMessage="DESDE" />
				</div>
				<div className="col-6">
					<FormattedMessage id="gravatarOption.date_filter_period.label_hasta" defaultMessage="HASTA" />
				</div>
				<div className="col-12" style={{ display: 'flex' }}>
					<div className="from-group" style={{ display: 'flex', marginLeft: 0, marginRight: '0.5rem' }}>
						<DatePicker
							selected={inicio ? moment(inicio) : null}
							className="form-control date-picker-placeholder datepicker-on-select"
							dateFormat={LocaleFormat.getDatePickerFormat()}
							selectsStart
							startDate={moment(inicio)}
							endDate={moment(fin)}
							openToDate={inicio ? moment(inicio) : moment()}
							onChange={(fecha) => setInicio(fecha ? fecha.format(FORMATO) : '')}
							popperPlacement="top-start"
							popperModifiers={popperModifiers}
						/>
					</div>
					<div className="from-group" style={{ display: 'flex', marginLeft: '0.5rem', marginRight: 0 }}>
						<DatePicker
							selected={fin ? moment(fin) : moment()}
							className="form-control date-picker-placeholder datepicker-on-select"
							dateFormat={LocaleFormat.getDatePickerFormat()}
							selectsEnd
							startDate={moment(inicio)}
							endDate={fin ? moment(fin) : moment()}
							openToDate={fin ? moment(fin) : moment()}
							minDate={moment(inicio)}
							onChange={(fecha) => setFin(fecha ? fecha.format(FORMATO) : '')}
							popperPlacement="top-end"
							popperModifiers={popperModifiers}
						/>
					</div>
				</div>
			</div>
			<div className="from-group" style={{ display: 'flex' }}>
				<button
					type="button"
					className="btn btn-primary"
					style={{ marginTop: 5, marginLeft: 'auto', marginRight: '0.5em', width: '48%' }}
					onClick={aplicar}
				>
					<i className="fa fa-check-circle"></i>
					<FormattedMessage id="gravatarOption.date_filter_period.button_aplicar" defaultMessage=" Aplicar" />
				</button>
				<button
					type="button"
					className="btn btn-danger"
					style={{ marginTop: 5, marginLeft: '0.5em', marginRight: 'auto', width: '48%' }}
					onClick={cancelar}
				>
					<i className="fa fa-times-circle"></i>
					<FormattedMessage id="gravatarOption.date_filter_period.button_cancelar" defaultMessage=" Cancelar" />
				</button>
			</div>
		</div>
	);
};

const PeriodoAutocomplete = (props) => {
	const [open, setOpen] = useState(false);

	const elegir = (option) => {
		props.onChange(option);
		setOpen(false);
	};

	return (
		<Autocomplete
			size="small"
			name={props.name}
			disabled={props.disabled}
			options={props.options || []}
			value={props.value ? props.value : null}
			getOptionLabel={(option) => (option ? option.label : '')}
			open={open}
			onOpen={() => setOpen(true)}
			onClose={() => setOpen(false)}
			// La fila del personalizado NO selecciona al clickearla: es un formulario, y se
			// aplica con su propio botón. Por eso se ignora el onChange de las opciones sin
			// label (la del personalizado es la única).
			onChange={(_, option) => {
				if (option === null || (option && option.label !== '')) elegir(option);
			}}
			disableCloseOnSelect
			renderInput={(params) => (
				<TextField
					{...params}
					variant="standard"
					label={props.label}
					placeholder={props.placeholder}
					onBlur={() => setOpen(false)}
				/>
			)}
			renderOption={(option) =>
				option.id === PERIODO_PERSONALIZADO ? (
					// onMouseDown detenido para que abrir un DatePicker adentro del desplegable
					// no cuente como elegir la opción ni cierre la lista.
					<div style={{ width: '100%' }} onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}>
						<FilaPersonalizada onAplicar={elegir} />
					</div>
				) : (
					option.label
				)
			}
		/>
	);
};

export default PeriodoAutocomplete;
